import { useNavigate, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import { Logo } from '../components/icons';
import { Note, ThemeSwitch } from '../components/ui';
import { ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';

/**
 * The sign-in panel's art: a quiet street grid of Jourian in vector lines, one aqua route with a dot gliding along
 * it, a pickup ring and a drop square. No raster, no figures. The dot's glide is off under reduced motion.
 */
function SignInArt() {
  const route = 'M140 690 C 260 640, 300 560, 420 540 S 600 470, 660 380 S 820 250, 930 230';
  return (
    <svg className="art" viewBox="0 0 1000 900" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <defs>
        <radialGradient id="glow" cx="70%" cy="30%" r="60%">
          <stop offset="0" stopColor="#5CE1E6" stopOpacity="0.16" />
          <stop offset="1" stopColor="#5CE1E6" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="fade" x1="0" y1="1" x2="0.35" y2="0">
          <stop offset="0" stopColor="#0D0D0D" stopOpacity="0.92" />
          <stop offset="0.55" stopColor="#0D0D0D" stopOpacity="0" />
        </linearGradient>
      </defs>
      <rect width="1000" height="900" fill="#0D0D0D" />
      <rect width="1000" height="900" fill="url(#glow)" />
      <g stroke="#FFFFFF" strokeOpacity="0.06" strokeWidth="1.5" fill="none">
        {Array.from({ length: 14 }, (_, i) => (
          <path key={`h${i}`} d={`M-20 ${60 + i * 64} Q 500 ${40 + i * 64 + (i % 3) * 18} 1020 ${70 + i * 64}`} />
        ))}
        {Array.from({ length: 12 }, (_, i) => (
          <path key={`v${i}`} d={`M${40 + i * 88} -20 Q ${60 + i * 88 + (i % 2) * 24} 450 ${30 + i * 88} 920`} />
        ))}
      </g>
      {/* the Chenab: one soft band */}
      <path d="M-40 820 C 200 760, 380 800, 560 740 S 860 700, 1040 640" stroke="#123B3E" strokeWidth="46" fill="none" strokeLinecap="round" />
      <path d={route} stroke="#0D0D0D" strokeWidth="10" fill="none" strokeLinecap="round" />
      <path d={route} stroke="#5CE1E6" strokeWidth="4" fill="none" strokeLinecap="round" />
      <circle cx="140" cy="690" r="11" fill="#0D0D0D" stroke="#F5F5F3" strokeWidth="5" />
      <rect x="919" y="219" width="22" height="22" rx="5" fill="#5CE1E6" stroke="#0D0D0D" strokeWidth="3" />
      <circle r="9" fill="#F5F5F3" className="route-glide" style={{ offsetPath: `path('${route}')` }} />
      {[
        [300, 300],
        [520, 650],
        [760, 560],
        [610, 210],
        [230, 470],
      ].map(([x, y]) => (
        <circle key={`${x}-${y}`} cx={x} cy={y} r="5" fill="#5CE1E6" fillOpacity="0.35" />
      ))}
      <rect width="1000" height="900" fill="url(#fade)" />
    </svg>
  );
}

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const search = useSearch({ from: '/login' });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <div className="login">
      <div className="login-art">
        <SignInArt />
        <div className="brand">
          <Logo size={40} />
          <div>
            <div className="brand-name">Zoomo Rides</div>
            <div className="brand-sub">Admin</div>
          </div>
        </div>
        <div className="login-copy">
          <h2>
            Every ride, driver and rupee.
            <br />
            <em>Live.</em>
          </h2>
          <p>Jourian, Akhnoor and Jammu — bikes, autos and cars, on one screen for the Zoomo Rides team.</p>
        </div>
      </div>
      <div className="login-form">
        <div className="login-card stack" style={{ gap: 22 }}>
          <div className="stack-xs">
            <h1>Sign in</h1>
            <p className="muted">Staff sign-in</p>
          </div>
          <form
            className="stack-sm"
            style={{ gap: 16 }}
            onSubmit={async (e) => {
              e.preventDefault();
              setBusy(true);
              setError(null);
              try {
                await login(email.trim(), password);
                const to = search.redirect?.startsWith('/') && !search.redirect.startsWith('//') ? search.redirect : '/';
                await navigate({ to, replace: true });
              } catch (err) {
                setError(
                  err instanceof ApiError && err.code === 'BAD_CREDENTIALS'
                    ? 'That email and password don’t match a staff account.'
                    : err instanceof ApiError && err.code === 'RATE_LIMITED'
                      ? 'Too many tries. Wait a minute and try again.'
                      : err instanceof Error
                        ? err.message
                        : 'Could not sign in.',
                );
                setBusy(false);
              }
            }}
          >
            <label className="field">
              <span className="field-label">Email</span>
              <input className="input" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
            </label>
            <label className="field">
              <span className="field-label">Password</span>
              <input
                className="input"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>
            {error && <Note tone="bad">{error}</Note>}
            <button className="btn btn-primary btn-lg btn-block" style={{ marginTop: 4 }} disabled={busy}>
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
          <div className="spread">
            <span className="small faint">Staff accounts are made by the owner.</span>
            <ThemeSwitch />
          </div>
        </div>
      </div>
    </div>
  );
}
